import { findIndex } from "@dhx/ts-common/core";
import { locate } from "@dhx/ts-common/html";
import { keyManager } from "@dhx/ts-common/Keymanager";
import { TreeCollection } from "@dhx/ts-data";
import { Tree } from "./Tree";
import { Id } from "./types";

interface INavigationStore {
	[id: string]: Tree;
}

export interface IKeyNavigation {
	add(id: Id, target: Tree): void;
}

class KeyNavigation implements IKeyNavigation {
	private _store: INavigationStore = {};
	private _keyManager = keyManager;
	private _activeTarget: Id;
	private _listen: boolean = false;
	private _blocked: boolean = false;

	add(id: Id, target: Tree): void {
		if (!this._listen) {
			this._listen = true;
			this._initKeys();
			this._addListeners();
		}
		this._store[id] = target;
	}
	block(val: boolean): void {
		this._blocked = val;
	}
	private _initKeys(): void {
		this._keyManager.addHotKey("arrowleft", (e) => {
			if (this._blocked) {
				return;
			}
			e.preventDefault();
			const target = this._store[this._activeTarget];
			if (!target) {
				return;
			}

			const focused = this._getFocused();
			const parent = target.data.getParent(focused);

			if (target.data.getRoot() === parent) {
				target.collapse(focused);
				return;
			}
			const isClosed = !target.data.getItem(focused).opened;

			if (isClosed) {
				target.focusItem(parent);
			} else {
				if (focused !== target.data.getRoot()) {
					target.collapse(focused);
				}
			}
		});
		this._keyManager.addHotKey("arrowright", (e) => {
			if (this._blocked) {
				return;
			}
			e.preventDefault();
			const target = this._store[this._activeTarget];
			if (!target) {
				return;
			}

			const focused = this._getFocused();
			if (target.data.haveItems(focused)) {
				target.expand(focused);
			}

		});
		this._keyManager.addHotKey("arrowup", (e) => {
			if (this._blocked) {
				return;
			}
			e.preventDefault();
			const target = this._store[this._activeTarget];
			if (!target) {
				return;
			}

			const focused = this._getFocused();
			const data = target.data;
			const next = this._getClosestTop(focused, data);
			if (next) {
				target.focusItem(next);
			}
		});
		this._keyManager.addHotKey("arrowdown", (e) => {
			if (this._blocked) {
				return;
			}
			e.preventDefault();
			const target = this._store[this._activeTarget];
			if (!target) {
				return;
			}

			const focused = this._getFocused();
			const data = target.data;
			const next = this._getClosestBot(focused, data);
			if (next) {
				target.focusItem(next);
			}
		});
		this._keyManager.addHotKey("enter", () => {
			if (this._blocked) {
				return;
			}
			const target = this._store[this._activeTarget];
			if (!target) {
				return;
			}

			const focused = this._getFocused();
			if (focused) {
				target.selection.add(focused);
			}
		});
	}
	private _getFocused(): Id {
		const target = this._store[this._activeTarget];
		const focused = (target as any)._focusId;
		if (focused) {
			return focused;
		} else {
			const root = target.data.getRoot();
			return target.data.getItems(root)[0].id;
		}
	}
	private _addListeners() {
		document.addEventListener("click", e => {
			const id = locate(e, "dhx_widget_id");
			if (id in this._store) {
				this._activeTarget = id;
			}
		});
		keyManager.addHotKey("tab", e => {
			const id = locate(e, "dhx_widget_id");
			if (id in this._store) {
				this._activeTarget = id;
			}
		}, this);
	}
	private _getClosestBot(id, data, ignore = false) {
		const item = data.getItem(id);
		const haveItems = data.haveItems(id);
		if (haveItems && item.opened && !ignore) { // item opened
			return data.getItems(id)[0].id;
		} else { // item not opened
			const parent = data.getParent(id);
			const childs = data.getItems(parent);
			const index = findIndex(childs, child => child.id === id);
			const len = childs.length;
			if (index + 1 < len) { // item have next element on this level
				return childs[index + 1].id;
			} else {
				if (parent === data.getRoot()) {
					return null;
				}
				return this._getClosestBot(parent, data, true);
			}
		}
	}
	private _getClosestTop(id: Id, data: TreeCollection) {
		const index = data.getIndex(id);
		const parent = data.getParent(id);

		if (index > 0) {
			const childs = data.getItems(parent);
			let current = childs[index - 1];
			if (!data.haveItems(current.id) || !current.opened) {
				return current.id;
			}

			while (data.haveItems(current.id) && current.opened) {
				const allChilds = data.getItems(current.id);
				current = allChilds[allChilds.length -1];
			}
			return current.id;
		} else {
			if (parent === data.getRoot()) {
				return null;
			}
			return parent;
		}
	}
}

export const keyNavigation = new KeyNavigation();